/**
 * SPP Plugin Component
 *
 * Renders SPP video player using Module Federation to load the remote SPPPlayer component.
 * The remote entry URL is resolved at runtime from REMOTE_URLS based on the env prop.
 */

import React, { Suspense, useCallback, useState } from "react";
import type { Media } from "@bronte/viewer";
import {
  COMPONENT,
  DEVICE_TYPE,
  isNativeMobileDeviceType,
  SPP_PLUGIN_ID,
} from "../../../constants";
import { StageElement, type ViewerDeviceRuntime } from "../../../types";
import { InstructionsBlock, StyledDiv } from "../../styles";
import { SppFigure, SppLabelSection } from "./styles";
import {
  getOfflinePackageImageFileName,
  getOfflinePackageImageUrl,
  isRemoteMediaAssetUrl,
  resolvePackageAssetPath,
} from "../../../util/resolveOfflinePackageImageUrl";
import { getRemoteSPP } from "./loadSpp";
import { SppMobileAudioThumbnail } from "./SppMobileAudioThumbnail";
import { SppMobileVideoThumbnail } from "./SppMobileVideoThumbnail";

interface SppPluginComponentProps {
  element: StageElement;
  componentConfig?: any;
  componentType?: string;
  env?: string;
  viewerDevice?: ViewerDeviceRuntime;
  onMediaClick?: (data: Media) => void;
}

const NATIVE_MEDIA_OVERLAY_STYLE: React.CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  height: "100%",
  width: "100%",
  zIndex: 9999,
};

type SppMediaClickOverlayProps = {
  children: React.ReactNode;
  enabled: boolean;
  onOverlayClick: () => void;
};

const SppMediaClickOverlay: React.FC<SppMediaClickOverlayProps> = ({
  children,
  enabled,
  onOverlayClick,
}) => {
  const handleOverlayActivate = useCallback(
    (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      event.preventDefault();
      onOverlayClick();
    },
    [onOverlayClick],
  );

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <div className="spp-media-overlay-container" style={{ position: "relative" }}>
      <div
        role="button"
        tabIndex={-1}
        aria-hidden
        className="spp-media-overlay"
        onClick={handleOverlayActivate}
        onTouchEnd={handleOverlayActivate}
        style={NATIVE_MEDIA_OVERLAY_STYLE}
      />
      <div style={{ pointerEvents: "none" }}>{children}</div>
    </div>
  );
};

const VIDEO_ASPECT_RATIO = "16 / 9";
const AUDIO_RESERVED_HEIGHT = 35;

/** Normalizes Bronte thumbnail config (string or image object with src) to a URL string. */
export function normalizeThumbnailUrl(thumbnail: unknown): string | undefined {
  if (typeof thumbnail === "string") {
    const trimmed = thumbnail.trim();
    return trimmed || undefined;
  }

  if (thumbnail && typeof thumbnail === "object" && "src" in thumbnail) {
    const src = (thumbnail as { src?: unknown }).src;
    if (typeof src === "string") {
      const trimmed = src.trim();
      return trimmed || undefined;
    }
  }

  return undefined;
}

/** Converts Pearson _video.true media URLs to _pmd.true thumbnail URLs (web / HTTP fallback). */
export function getVideoThumbnailUrl(rawUrl: string): string | null {
  if (!/_video\.true\//.test(rawUrl)) {
    return null;
  }

  const pmdUrl = rawUrl.replace("/_video.true/", "/_pmd.true/");
  if (pmdUrl.includes("mimeType=")) {
    return pmdUrl;
  }

  return `${pmdUrl}?mimeType=jpg`;
}

/** Builds a local offline thumbnail path under package assets/images/original. */
export function getOfflineVideoThumbnailUrl(
  packagePath: string,
  rawUrl: string,
  thumbnailURL?: unknown,
): string | null {
  const configuredThumbnail = normalizeThumbnailUrl(thumbnailURL);

  if (configuredThumbnail && !isRemoteMediaAssetUrl(configuredThumbnail)) {
    return configuredThumbnail;
  }

  const resolvedPackagePath = resolvePackageAssetPath(packagePath);
  if (rawUrl.startsWith(resolvedPackagePath) && rawUrl.includes("/assets/images/")) {
    return rawUrl;
  }

  const imageName =
    getOfflinePackageImageFileName(configuredThumbnail || "") ||
    getOfflinePackageImageFileName(rawUrl);
  if (!imageName) {
    return getOfflinePackageImageUrl(packagePath, rawUrl);
  }

  return `${resolvedPackagePath}/assets/images/original/${imageName}`;
}

export function resolveMobileVideoThumbnailUrl(
  rawUrl: string,
  packagePath: string | undefined,
  usePackageThumbnail: boolean,
  thumbnailURL?: unknown,
): string | null {
  if (usePackageThumbnail) {
    if (!packagePath) {
      return null;
    }

    return getOfflineVideoThumbnailUrl(packagePath, rawUrl, thumbnailURL);
  }

  const configuredThumbnail = normalizeThumbnailUrl(thumbnailURL);
  if (configuredThumbnail) {
    return configuredThumbnail;
  }

  return getVideoThumbnailUrl(rawUrl);
}

const VideoFallback: React.FC = () => (
  <div
    style={{
      width: "100%",
      height: "100%",
      aspectRatio: VIDEO_ASPECT_RATIO,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    Loading video player...
  </div>
);

const AudioFallback: React.FC = () => (
  <div
    style={{
      width: "100%",
      height: `${AUDIO_RESERVED_HEIGHT}px`,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    Loading audio player...
  </div>
);

export const SppPluginComponent: React.FC<SppPluginComponentProps> = ({
  element: _element,
  componentConfig,
  componentType,
  env,
  viewerDevice,
  onMediaClick,
}) => {
  const deviceType = viewerDevice?.deviceType ?? DEVICE_TYPE.WEB;
  const packagePath = viewerDevice?.packagePath;
  const RemoteSPP = getRemoteSPP(env ?? "dev");
  const [isPlayerReady, setIsPlayerReady] = useState<boolean>(false);
  const config = componentConfig?.config || componentConfig || {};
  const sppEnvironment = (env ?? "dev").toLowerCase();
  const rawUrl = config.URL || config.url || config.source || "";
  const thumbnailURL = config.thumbnailURL || config.thumbnailUrl;
  // Remove _video.true and _pmd.true path segments from Pearson media URLs for SPPPlayer
  const url = rawUrl.replace(/\/(_video\.true|_pmd\.true)\//g, "/");
  const activityTitle = config.activityTitle || config.displaytitle;
  const activityLabel = config.activityLabel || config.label;
  const activityInstructions = config.instructions || config.instText;
  const pluginId = componentConfig?.pluginPayload?.summary?.pluginId || componentConfig?.$id;
  // Audio plugin has no schema mediaType — force audio when id is SPP Audio Player
  const isAudio =
    pluginId === SPP_PLUGIN_ID.AUDIO ||
    config.mediaType?.toLowerCase() === "audio" ||
    config.mediaType?.toLowerCase() === "voice";
  const mediaType: Media["mediaType"] = isAudio ? "Audio" : "Video";
  const showNativeMediaOverlay = isNativeMobileDeviceType(deviceType);
  const usePackageVideoThumbnail = showNativeMediaOverlay && !isAudio;
  const videoThumbnailUrl = resolveMobileVideoThumbnailUrl(
    rawUrl,
    packagePath,
    usePackageVideoThumbnail,
    thumbnailURL,
  );
  const useMobileVideoThumbnail = showNativeMediaOverlay && !isAudio && videoThumbnailUrl !== null;
  const useMobileAudioThumbnail = showNativeMediaOverlay && isAudio;

  const handleMediaClick = useCallback(() => {
    if (!onMediaClick || !url) {
      return;
    }
    onMediaClick({
      mediaType,
      metadata: { url },
    } as Media);
  }, [mediaType, onMediaClick, url]);

  // MMI config fields
  const caption = config.caption;
  const credit = config.credit;

  const componentId = componentConfig.$id || "";
  const isMmi = componentType === COMPONENT.MMI;
  const labelDataId = componentId
    ? `${componentId}-${isMmi ? "label" : "activityLabel"}`
    : undefined;
  const titleDataId = componentId
    ? `${componentId}-${isMmi ? "displaytitle" : "activityTitle"}`
    : undefined;
  const instructionsDataId = componentId
    ? `${componentId}-${isMmi ? "instText" : "instructions"}`
    : undefined;

  return (
    // The MMI plugin's root is `div.mediaPlayer`; the theme keys the label's
    // camera icon (`.mediaPlayer h4 span.component-label p::before`) and label
    // font off it.
    <SppFigure className={isMmi ? "textbook-video mediaPlayer" : "textbook-video"}>
      {(activityLabel || activityTitle) && (
        <SppLabelSection className="component-label-section">
          {activityLabel && (
            <span
              className="component-label"
              data-id={labelDataId}
              dangerouslySetInnerHTML={{ __html: activityLabel }}
            />
          )}
          {activityTitle && (
            <span
              className="component-title"
              data-id={titleDataId}
              dangerouslySetInnerHTML={{ __html: activityTitle }}
            />
          )}
        </SppLabelSection>
      )}
      {activityInstructions && (
        <InstructionsBlock
          className="component-instructions"
          data-id={instructionsDataId}
          dangerouslySetInnerHTML={{ __html: activityInstructions }}
        />
      )}
      {isAudio ? (
        <div
          data-id={componentId || undefined}
          style={{
            position: "relative",
            aspectRatio: useMobileAudioThumbnail ? "600 / 399" : undefined,
          }}
        >
          <SppMediaClickOverlay enabled={showNativeMediaOverlay} onOverlayClick={handleMediaClick}>
            {useMobileAudioThumbnail ? (
              <SppMobileAudioThumbnail />
            ) : (
              <Suspense fallback={<AudioFallback />}>
                <RemoteSPP url={url} isAudioPlayer environment={sppEnvironment} />
              </Suspense>
            )}
          </SppMediaClickOverlay>
        </div>
      ) : (
        <div
          className="spp-video-frame"
          data-id={componentId || undefined}
          style={{
            position: "relative",
            aspectRatio: useMobileVideoThumbnail || !isPlayerReady ? VIDEO_ASPECT_RATIO : "auto",
          }}
        >
          <SppMediaClickOverlay enabled={showNativeMediaOverlay} onOverlayClick={handleMediaClick}>
            {useMobileVideoThumbnail ? (
              <SppMobileVideoThumbnail src={videoThumbnailUrl} />
            ) : (
              <Suspense fallback={<VideoFallback />}>
                <RemoteSPP
                  url={url}
                  loadingAspectRatio
                  environment={sppEnvironment}
                  onReady={() => setIsPlayerReady(true)}
                />
              </Suspense>
            )}
          </SppMediaClickOverlay>
        </div>
      )}
      {caption && (
        <figcaption>
          <StyledDiv
            className="component-caption"
            data-id={`${componentId}-caption`}
            dangerouslySetInnerHTML={{ __html: caption }}
          />
        </figcaption>
      )}
      {credit && (
        <StyledDiv
          className="component-credit"
          data-id={`${componentId}-credit`}
          dangerouslySetInnerHTML={{ __html: credit }}
        />
      )}
    </SppFigure>
  );
};
